import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Mail, 
  Phone, 
  Globe, 
  MapPin, 
  Download,
  Share2,
  Linkedin,
  Twitter,
  Instagram,
  Github,
  Facebook,
  ExternalLink,
  User,
  Briefcase,
  Building2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  twitter: Twitter,
  instagram: Instagram,
  github: Github,
  facebook: Facebook
};

const CONTACT_COLORS = {
  emails: { bg: 'bg-primary/20', text: 'text-primary', hover: 'group-hover:text-primary' },
  phones: { bg: 'bg-emerald-500/20', text: 'text-emerald-500', hover: 'group-hover:text-emerald-500' },
  websites: { bg: 'bg-amber-500/20', text: 'text-amber-500', hover: 'group-hover:text-amber-500' },
  addresses: { bg: 'bg-rose-500/20', text: 'text-rose-500', hover: 'group-hover:text-rose-500' },
};

const CATEGORY_COLORS = {
  'Primary': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Secondary': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'Additional 1': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Additional 2': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Additional 3': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
};

const AffiliationCard = ({ affiliation, isExpanded = false }) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const categoryColor = CATEGORY_COLORS[affiliation.category] || CATEGORY_COLORS['Primary'];
  
  const hasContactInfo = affiliation.email || affiliation.phone || affiliation.website || affiliation.address;
  
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          {affiliation.company_logo ? (
            <img 
              src={affiliation.company_logo} 
              alt={affiliation.company}
              className="w-12 h-12 rounded-lg object-contain bg-white/10"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-slate-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={`text-xs ${categoryColor}`}>
                {affiliation.category}
              </Badge>
            </div>
            <h3 className="font-semibold text-white truncate">{affiliation.company || 'Company'}</h3>
            {affiliation.designation && (
              <p className="text-sm text-slate-400 flex items-center gap-1">
                <Briefcase className="w-3 h-3" />
                {affiliation.designation}
              </p>
            )}
          </div>
          {hasContactInfo && (
            <div className="text-slate-500">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          )}
        </div>
      </div>
      
      {expanded && hasContactInfo && (
        <div className="px-4 pb-4 space-y-2 border-t border-white/10 pt-3">
          {affiliation.email && (
            <a 
              href={`mailto:${affiliation.email}`}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors group"
            >
              <Mail className="w-4 h-4 text-primary" />
              <span className="text-sm text-slate-300 group-hover:text-white">{affiliation.email}</span>
            </a>
          )}
          {affiliation.phone && (
            <a 
              href={`tel:${affiliation.phone}`}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors group"
            >
              <Phone className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-slate-300 group-hover:text-white">{affiliation.phone}</span>
            </a>
          )}
          {affiliation.website && (
            <a 
              href={affiliation.website.startsWith('http') ? affiliation.website : `https://${affiliation.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors group"
            >
              <Globe className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-slate-300 group-hover:text-white truncate">
                {affiliation.website.replace(/^https?:\/\//, '')}
              </span>
              <ExternalLink className="w-3 h-3 text-slate-500" />
            </a>
          )}
          {affiliation.address && (
            <a 
              href={`https://maps.google.com/?q=${encodeURIComponent(affiliation.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors group"
            >
              <MapPin className="w-4 h-4 text-rose-500" />
              <span className="text-sm text-slate-300 group-hover:text-white">{affiliation.address}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const ContactSection = ({ items, type, icon: Icon, getHref, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const colors = CONTACT_COLORS[type];
  
  if (!items || items.length === 0) return null;
  
  const primaryItem = items[0];
  const hasMore = items.length > 1;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <a 
        href={getHref(primaryItem.value)}
        target={type === 'websites' ? '_blank' : undefined}
        rel={type === 'websites' ? 'noopener noreferrer' : undefined}
        className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group"
        data-testid={`contact-${type}-0`}
      >
        <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${colors.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 uppercase tracking-wide">
            {primaryItem.label || label}
          </p>
          <p className={`text-white truncate ${colors.hover} transition-colors`}>
            {type === 'websites' ? primaryItem.value.replace(/^https?:\/\//, '') : primaryItem.value}
          </p>
        </div>
        <ExternalLink className={`w-4 h-4 text-slate-500 ${colors.hover} transition-colors`} />
      </a>
      
      {hasMore && (
        <>
          <CollapsibleTrigger asChild>
            <button 
              className="w-full flex items-center justify-center gap-1 py-2 text-xs text-slate-400 hover:text-white transition-colors"
              data-testid={`expand-${type}`}
            >
              {isOpen ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  +{items.length - 1} more {label.toLowerCase()}
                </>
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2">
            {items.slice(1).map((item, idx) => (
              <a 
                key={idx}
                href={getHref(item.value)}
                target={type === 'websites' ? '_blank' : undefined}
                rel={type === 'websites' ? 'noopener noreferrer' : undefined}
                className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group ml-4"
                data-testid={`contact-${type}-${idx + 1}`}
              >
                <div className={`w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${colors.text}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    {item.label || label}
                  </p>
                  <p className={`text-white text-sm truncate ${colors.hover} transition-colors`}>
                    {type === 'websites' ? item.value.replace(/^https?:\/\//, '') : item.value}
                  </p>
                </div>
                <ExternalLink className={`w-3 h-3 text-slate-500 ${colors.hover} transition-colors`} />
              </a>
            ))}
          </CollapsibleContent>
        </>
      )}
    </Collapsible>
  );
};

const PublicCardPage = () => {
  const { cardId } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/public/card/${cardId}`, {
          params: {
            user_agent: navigator.userAgent,
            referrer: document.referrer
          }
        });
        setCard(response.data);
      } catch (err) {
        setError('Card not found');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [cardId]);

  const handleSaveContact = () => {
    window.location.href = `${API_URL}/api/public/card/${cardId}/vcard`;
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${card.name}'s Contact Card`,
          text: `Check out ${card.name}'s digital business card`,
          url: shareUrl
        });
      } catch (err) {
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="max-w-sm w-full text-center p-8">
          <User className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Card Not Found</h2>
          <p className="text-muted-foreground">This card may have been deleted or doesn't exist.</p>
        </Card>
      </div>
    );
  }

  // Get data from multi-entry fields or legacy single fields
  const emails = card.personal_emails?.length > 0 ? card.personal_emails : 
                 (card.emails?.length > 0 ? card.emails : (card.email ? [{ label: 'Email', value: card.email }] : []));
  const phones = card.personal_phones?.length > 0 ? card.personal_phones :
                 (card.phones?.length > 0 ? card.phones : (card.phone ? [{ label: 'Phone', value: card.phone }] : []));
  const websites = card.personal_websites?.length > 0 ? card.personal_websites :
                   (card.websites?.length > 0 ? card.websites : (card.website ? [{ label: 'Website', value: card.website }] : []));
  const addresses = card.personal_addresses?.length > 0 ? card.personal_addresses :
                    (card.addresses?.length > 0 ? card.addresses : (card.address ? [{ label: 'Address', value: card.address }] : []));
  const jobTitles = card.job_titles?.length > 0 ? card.job_titles : (card.job_title ? [{ label: 'Title', value: card.job_title }] : []);
  
  // Get affiliations
  const affiliations = card.affiliations || [];
  const hasAffiliations = affiliations.length > 0;
  const hasPersonalContact = emails.length > 0 || phones.length > 0 || websites.length > 0 || addresses.length > 0;
  
  // Get primary affiliation for legacy display
  const primaryAffiliation = affiliations.find(a => a.category === 'Primary') || affiliations[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="public-card-wrapper relative z-10">
        {/* Card Container */}
        <div className="flex-1 flex flex-col">
          {/* Main Card */}
          <Card className="glass-dark rounded-3xl border-white/10 shadow-2xl overflow-hidden animate-fade-in" data-testid="public-card">
            <CardContent className="p-0">
              {/* Header with gradient */}
              <div className="h-32 bg-gradient-to-br from-primary via-primary/80 to-purple-600 relative">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
              </div>

              {/* Profile Section */}
              <div className="px-6 pb-6 -mt-16 relative">
                {/* Avatar */}
                <div className="mb-4">
                  {card.profile_photo ? (
                    <img 
                      src={card.profile_photo} 
                      alt={card.name}
                      className="w-28 h-28 rounded-full object-cover ring-4 ring-slate-800 shadow-xl"
                      data-testid="profile-photo"
                    />
                  ) : (
                    <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center ring-4 ring-slate-800 shadow-xl">
                      <span className="text-4xl font-bold text-white" data-testid="profile-initials">
                        {card.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Name & Titles */}
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-white mb-1" data-testid="card-name">{card.name}</h1>
                  
                  {/* Show primary affiliation info if available */}
                  {primaryAffiliation ? (
                    <div className="space-y-1">
                      {primaryAffiliation.designation && (
                        <p className="text-slate-300 flex items-center gap-2" data-testid="card-job-title">
                          <Briefcase className="w-4 h-4" />
                          {primaryAffiliation.designation}
                        </p>
                      )}
                      {primaryAffiliation.company && (
                        <p className="text-slate-400 flex items-center gap-2 text-sm" data-testid="card-company">
                          <Building2 className="w-4 h-4" />
                          {primaryAffiliation.company}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Legacy Job Titles */}
                      {jobTitles.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-slate-300 flex items-center gap-2" data-testid="card-job-title">
                            <Briefcase className="w-4 h-4" />
                            {jobTitles[0].value}
                          </p>
                          {jobTitles.length > 1 && (
                            <div className="flex flex-wrap gap-1 ml-6">
                              {jobTitles.slice(1).map((jt, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs bg-white/10 text-slate-300">
                                  {jt.value}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {card.company && (
                        <p className="text-slate-400 flex items-center gap-2 text-sm mt-1" data-testid="card-company">
                          <Building2 className="w-4 h-4" />
                          {card.company}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Bio */}
                {card.bio && (
                  <p className="text-slate-400 text-sm mb-6 leading-relaxed" data-testid="card-bio">
                    {card.bio}
                  </p>
                )}

                {/* Affiliations Section with Tabs/Accordion */}
                {hasAffiliations && (
                  <div className="mb-6">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Affiliations</p>
                    
                    {affiliations.length <= 2 ? (
                      // Show all affiliations expanded if 2 or less
                      <div className="space-y-3">
                        {affiliations.map((aff, idx) => (
                          <AffiliationCard 
                            key={aff.id || idx} 
                            affiliation={aff} 
                            isExpanded={idx === 0}
                          />
                        ))}
                      </div>
                    ) : (
                      // Use tabs for 3+ affiliations
                      <Tabs defaultValue={affiliations[0]?.id || '0'} className="w-full">
                        <TabsList className="w-full flex overflow-x-auto bg-white/5 p-1 rounded-xl mb-3">
                          {affiliations.map((aff, idx) => (
                            <TabsTrigger 
                              key={aff.id || idx} 
                              value={aff.id || String(idx)}
                              className="flex-1 min-w-[100px] text-xs data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400 rounded-lg py-2"
                            >
                              {aff.category}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                        {affiliations.map((aff, idx) => (
                          <TabsContent key={aff.id || idx} value={aff.id || String(idx)} className="mt-0">
                            <AffiliationCard affiliation={aff} isExpanded={true} />
                          </TabsContent>
                        ))}
                      </Tabs>
                    )}
                  </div>
                )}

                {/* Personal Contact Sections */}
                {hasPersonalContact && (
                  <div className="space-y-3 mb-6">
                    {hasAffiliations && (
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Personal Contact</p>
                    )}
                    <ContactSection 
                      items={emails}
                      type="emails"
                      icon={Mail}
                      getHref={(val) => `mailto:${val}`}
                      label="Email"
                    />
                    
                    <ContactSection 
                      items={phones}
                      type="phones"
                      icon={Phone}
                      getHref={(val) => `tel:${val}`}
                      label="Phone"
                    />
                    
                    <ContactSection 
                      items={websites}
                      type="websites"
                      icon={Globe}
                      getHref={(val) => val.startsWith('http') ? val : `https://${val}`}
                      label="Website"
                    />
                    
                    <ContactSection 
                      items={addresses}
                      type="addresses"
                      icon={MapPin}
                      getHref={(val) => `https://maps.google.com/?q=${encodeURIComponent(val)}`}
                      label="Address"
                    />
                  </div>
                )}

                {/* Social Links */}
                {card.social_links && card.social_links.length > 0 && (
                  <div className="mb-6">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Connect</p>
                    <div className="flex flex-wrap gap-3">
                      {card.social_links.map((link) => {
                        const Icon = SOCIAL_ICONS[link.platform] || Globe;
                        return (
                          <a
                            key={link.platform}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-12 h-12 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors group"
                            data-testid={`social-link-${link.platform}`}
                          >
                            <Icon className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="mt-6 space-y-3 animate-slide-up stagger-2">
            <Button 
              onClick={handleSaveContact}
              className="w-full h-14 text-base font-semibold bg-white text-slate-900 hover:bg-slate-100 rounded-xl shadow-lg"
              data-testid="save-contact-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Save Contact
            </Button>
            
            <Drawer>
              <DrawerTrigger asChild>
                <Button 
                  variant="outline"
                  className="w-full h-14 text-base font-semibold border-white/20 text-white hover:bg-white/10 rounded-xl"
                  data-testid="share-btn"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Share Card
                </Button>
              </DrawerTrigger>
              <DrawerContent className="bg-slate-900 border-slate-700">
                <DrawerHeader>
                  <DrawerTitle className="text-white">Share this card</DrawerTitle>
                  <DrawerDescription className="text-slate-400">
                    Share {card.name}'s contact card with others
                  </DrawerDescription>
                </DrawerHeader>
                <div className="px-4 pb-4 space-y-3">
                  <Button 
                    onClick={handleShare}
                    className="w-full h-12"
                    data-testid="native-share-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share via...
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => copyToClipboard(window.location.href)}
                    className="w-full h-12 border-slate-600 text-white hover:bg-slate-800"
                    data-testid="copy-link-btn"
                  >
                    Copy Link
                  </Button>
                </div>
                <DrawerFooter>
                  <DrawerClose asChild>
                    <Button variant="ghost" className="text-slate-400">Cancel</Button>
                  </DrawerClose>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-slate-600 text-xs">
              Powered by <span className="text-slate-500 font-medium">CardLink</span>
            </p>
          </div>
        </div>
      </div>
      
      <Toaster position="top-center" richColors />
    </div>
  );
};

export default PublicCardPage;
